import React, { useState, useEffect, useRef } from "react";
import { I18n } from "@shared/modules/utils/LangUtil";
import { FormField, ToastUtil } from "@shared/modules/utils";
import { ADD_BENE_STEPS, addBeneFieldConfigFunc, prepare } from "@app/modules/Landing/AddBeneficiary/constants";
import DataService from "@shared/services/DataService";
import DataStore from "@shared/services/DataStore";
import OtpOptionsPopup from "@shared/modules/utils/OtpOptionsPopup";
import MetaService from "@shared/services/MetaService";
import TenantUtils from "@shared/modules/utils/TenantUtils/TenantUtils";
import EventService from "@shared/services/EventService";
import OtpOptionSelectPopup from "@app/modules/Login/OtpOptionSelectPopup";
import FormGroupWrapper from "@app/modules/utils/FormGroupWrapper";

const DEFAULT_ACTIVATION_TIME = 30;
const DEFAULT_TX_LIMIT = 500;
const OTP_STATUSES = ["DOTP_REQUIRED", "EOTP_REQUIRED", "WOTP_REQUIRED", "MOTP_REQUIRED", "NOTP_REQUIRED"];
export const OTP_LIMIT_STATUSES = [
    "OTP_ATTEMPT_EXCEEDED_FLOW",
    "SEND_OTP_LIMIT_EXCEEDED_FLOW",
    "SEND_OTP_LIMIT_WAIT",
    "MISSING_OTP_CONTACT"
];

const ContactDetails = props => {
    const { history, goToStep, stepsData, setFlowComplete } = props;
    const countryAndChannelDet = stepsData[ADD_BENE_STEPS.CHANNEL_DETAILS.name] || {
        serviceGroupId: "2",
        beneficaryCountryId: "94"
    };
    const enableTown = countryAndChannelDet.metaParam?.townRender;

    const [loading, setLoading] = useState({});

    const [countryList, setCountryList] = useState([]);
    const countryFieldRef = useRef();

    const [stateList, setStateList] = useState([]);
    const stateFieldRef = useRef();

    const [districtList, setDistrictList] = useState([]);
    const districtFieldRef = useRef();

    const [cityList, setCityList] = useState([]);
    const cityFieldRef = useRef();

    const [townList, setTownList] = useState([]);
    const townFieldRef = useRef();

    const [mobileCodeList, setMobileCodeList] = useState([]);
    const mobileCodeFieldRef = useRef();

    const mobileNoFieldRef = useRef();

    const [beneAdded, setBeneAdded] = useState(false);

    const activeFieldRefs = useRef([]);

    useEffect(() => {
        activeFieldRefs.current = activeFieldRefs.current.filter(ref => ref.inputRef.current);
    }, [activeFieldRefs.current]);

    useEffect(() => {
        const init = async () => {
            fetchCountryList();
        };
        init();
    }, []);

    const fetchCountryList = async () => {
        try {
            setLoading(prev => ({ ...prev, fetchCountryList: true }));
            let res = await DataService.get("getCountryList");
            if (res?.statusKey === "SUCCESS") {
                let userCountry;
                let _countryList = [];
                let _mobileCodeList = [];

                res.data.map(v => {
                    let country = {
                        label: v.countryName,
                        value: v.countryId,
                        supportedServiceGroup: v.supportedServiceGroup,
                        countryAlpha2Code: v.countryAlpha2Code
                    };
                    if (countryAndChannelDet.beneficaryCountryId === country.value) {
                        userCountry = country;
                    }
                    _countryList.push(country);

                    if (v.countryTelCode) {
                        _mobileCodeList.push({
                            label: `+${v.countryTelCode} - ${v.countryName}`,
                            value: v.countryTelCode,
                            countryId: v.countryId
                        });
                    }
                });

                setCountryList(_countryList);
                setMobileCodeList(_mobileCodeList);
                if (userCountry) {
                    await countryFieldRef.current.val(userCountry);
                    handleCountryFieldChange(userCountry);

                    let userMobileCode = _mobileCodeList.find(m => m.countryId === userCountry.value);
                    if (userMobileCode) await mobileCodeFieldRef.current.val(userMobileCode);
                }
            } else {
                throw res;
            }
        } catch (error) {
            console.error("fetchCountryList failed", error);
        } finally {
            setLoading(prev => ({ ...prev, fetchCountryList: false }));
            return {};
        }
    };
    const handleCountryFieldChange = async newVal => {
        console.log("handleCountryFieldChange", newVal);

        //integrate the dynamic flow here 
        //Dynamic Canada Field Flow
        resetFields("country");
        if (newVal) {
            fetchStateList();
            let userMobileCode = mobileCodeList.find(m => m.countryId === newVal.value);
            if (userMobileCode) await mobileCodeFieldRef.current.val(userMobileCode);
        }
    };

    const fetchStateList = async () => {
        try {
            setLoading(prev => ({ ...prev, fetchStateList: true }));
            let res = await DataService.post(
                "getStateList",
                { ...(countryAndChannelDet.metaParam || {}) },
                {
                    params: { countryId: countryFieldRef.current.val().value }
                }
            );
            if (res?.statusKey === "SUCCESS") {
                let _stateList = res.data.map(v => ({
                    label: v.stateName,
                    value: v.stateIdStr || result.stateId
                }));
                setStateList(_stateList);
            } else {
                throw res;
            }
        } catch (error) {
            console.error("fetchStateList failed", error);
        } finally {
            setLoading(prev => ({ ...prev, fetchStateList: false }));
            return {};
        }
    };
    const handleStateFieldChange = async newVal => {
        console.log("handleStateFieldChange", newVal);

        resetFields("state");
        if (newVal) fetchDistrictList();
    };

    const fetchDistrictList = async () => {
        try {
            setLoading(prev => ({ ...prev, fetchDistrictList: true }));
            let res = await DataService.post(
                "getDistrictList",
                { ...(countryAndChannelDet.metaParam || {}) },
                {
                    params: {
                        countryId: countryFieldRef.current.val().value,
                        stateId: stateFieldRef.current.val().value
                    }
                }
            );
            if (res?.statusKey === "SUCCESS") {
                let _districtList = res.data.map(v => ({
                    label: v.districtDesc,
                    value: v.districtIdStr || v.districtId
                }));
                setDistrictList(_districtList);
            } else {
                throw res;
            }
        } catch (error) {
            console.error("fetchDistrictList failed", error);
        } finally {
            setLoading(prev => ({ ...prev, fetchDistrictList: false }));
            return {};
        }
    };
    const handleDistrictFieldChange = async newVal => {
        console.log("handleDistrictFieldChange", newVal);

        resetFields("district");
        if (newVal) fetchCityList();
    };

    const fetchCityList = async () => {
        try {
            setLoading(prev => ({ ...prev, fetchCityList: true }));
            let res = await DataService.post(
                "getCityList",
                { ...(countryAndChannelDet.metaParam || {}) },
                {
                    params: {
                        countryId: countryFieldRef.current.val().value,
                        stateId: stateFieldRef.current.val().value,
                        districtId: districtFieldRef.current.val().value
                    }
                }
            );
            if (res?.statusKey === "SUCCESS") {
                let _cityList = res.data.map(v => ({
                    label: v.cityName,
                    value: v.idNoStr || v.cityMasterId
                }));
                setCityList(_cityList);
            } else {
                throw res;
            }
        } catch (error) {
            console.error("fetchCityList failed", error);
        } finally {
            setLoading(prev => ({ ...prev, fetchCityList: false }));
            return {};
        }
    };
    const handleCityFieldChange = async newVal => {
        console.log("handleCityFieldChange", newVal);

        resetFields("city");
        if (newVal) fetchTownList();
    };

    const fetchTownList = async () => {
        try {
            setLoading(prev => ({ ...prev, fetchTownList: true }));
            let res = await DataService.post(
                "getTownList",
                { ...(countryAndChannelDet.metaParam || {}) },
                {
                    params: {
                        countryId: countryFieldRef.current.val().value,
                        stateId: stateFieldRef.current.val().value,
                        districtId: districtFieldRef.current.val().value,
                        cityId: cityFieldRef.current.val().value
                    }
                }
            );
            if (res?.statusKey === "SUCCESS") {
                let _townList = res.data.map(v => ({
                    label: v.townName,
                    value: v.townId
                }));
                setTownList(_townList);
            } else {
                throw res;
            }
        } catch (error) {
            console.error("fetchTownList failed", error);
        } finally {
            setLoading(prev => ({ ...prev, fetchTownList: false }));
            return {};
        }
    };
    const handleTownFieldChange = async newVal => {
        console.log("handleTownFieldChange", newVal);
    };

    const handleMobileCodeFieldChange = async newVal => {
        console.log("handleMobileCodeFieldChange", newVal);
    };

    const handleMobileNoFieldChange = async newVal => {
        console.log("handleMobileNoFieldChange", newVal);
    };

    const resetFields = field => {
        const resetFunctions = {
            state: () => {
                stateFieldRef.current?.val("");
                setStateList([]);
            },
            district: () => {
                districtFieldRef.current?.val("");
                setDistrictList([]);
            },
            city: () => {
                cityFieldRef.current?.val("");
                setCityList([]);
            },
            town: () => {
                townFieldRef.current?.val("");
                setTownList([]);
            }
        };
        const resetFieldsMap = {
            country: ["state", "district", "city", "town"],
            state: ["district", "city", "town"],
            district: ["city", "town"],
            city: ["town"]
        };
        const resetFieldsList = resetFieldsMap[field];
        if (resetFieldsList) {
            resetFieldsList.map(f => {
                let func = resetFunctions[f];
                func();
            });
        }
    };

    const isValid = () => {
        if (false) {
            // if customFields
            if (
                [
                    // ...(false
                    //   ? [
                    //       // if non-individual
                    //       this.BENE_INSTITUTION_NAME.isValid(),
                    //       this.BENE_INSTITUTION_NAME_LOCAL.isValid(),
                    //       this.BENE_INSTITUTION_CATEGORY.isValid(),
                    //     ]
                    //   : [
                    //       // if individual
                    //       this.beneCustomFields.isValid(),
                    //       this.relationshipSelect.isValid(),
                    //       this.nationalitySelect.isValid(),
                    //     ]),
                    // this.countrySelect.isValid(),
                    // this.stateSelect.isValid(),
                    // this.districtSelect.isValid(),
                    // this.citySelect.isValid(),
                    // this.townSelect ? this.townSelect.isValid() : true,
                    // this.mobileCodeInput.isValid(),
                    // this.mobileNoInput.isValid(),
                ].indexOf(false) !== -1
            )
                return false;
        } else {
            // no custom fields
            if (
                [
                    // ...(false
                    //   ? [
                    //       // if non-individual
                    //       this.BENE_INSTITUTION_NAME.isValid(),
                    //       this.BENE_INSTITUTION_CATEGORY.isValid(),
                    //     ]
                    //   : [
                    //       // if individual
                    //       this.BENE_FIRST_NAME.isValid(),
                    //       this.lastNameInput.isValid(),
                    //       this.relationshipSelect.isValid(),
                    //       this.nationalitySelect.isValid(),
                    //     ]),
                    // this.countrySelect.isValid(),
                    countryFieldRef.current.isValid(),
                    // this.stateSelect.isValid(),
                    stateFieldRef.current.isValid(),
                    // this.districtSelect.isValid(),
                    districtFieldRef.current.isValid(),
                    // this.citySelect.isValid(),
                    cityFieldRef.current.isValid(),
                    // this.townSelect ? this.townSelect.isValid() : true,
                    townFieldRef.current ? townFieldRef.current.isValid() : true,
                    // this.mobileCodeInput.isValid(),
                    mobileCodeFieldRef.current.isValid(),
                    // this.mobileNoInput.isValid()
                    mobileNoFieldRef.current.isValid()
                ].indexOf(false) !== -1
            )
                return false;
        }
        return true;
    };

    const val = () => {
        return {
            // beneficaryTypeId: this.state.isOptIn ? 2 : 1,
            // ...(false // if customFields
            //   ? {
            //       ...(false // if non-individual
            //         ? {
            //             institutionName: this.BENE_INSTITUTION_NAME.getValue(),
            //             institutionNameLocal:
            //               this.BENE_INSTITUTION_NAME_LOCAL.getValue(),
            //             institutionCategoryId:
            //               this.BENE_INSTITUTION_CATEGORY.getValue().value,
            //           }
            //         : {
            //             // if individual
            //             ...this.beneCustomFields.val(),
            //           }),
            //     }
            //   : {
            //       ...(false // if non-individual
            //         ? {
            //             institutionCategoryId:
            //               this.BENE_INSTITUTION_CATEGORY.getValue().value,
            //             institutionName: this.BENE_INSTITUTION_NAME.getValue(),
            //           }
            //         : {
            //             // if individual
            //             firstName: this.BENE_FIRST_NAME.getValue(),
            //             secondName: this.lastNameInput.getValue(),
            //           }),
            //     }),
            // ...(false // if non-individual
            //   ? {}
            //   : {
            //       // if individual
            //       nationality: this.nationalitySelect.getValue().value,
            //       relationsId: this.relationshipSelect.getValue().value,
            //     }),
            countryId: countryFieldRef.current.val().value,
            stateId: stateFieldRef.current.val().value,
            districtId: districtFieldRef.current.val().value,
            cityId: cityFieldRef.current.val().value,
            ...(townFieldRef.current ? { townId: townFieldRef.current.val().value } : {}),
            countryTelCode: mobileCodeFieldRef.current.val().value,
            mobileNumber: mobileNoFieldRef.current.val()
        };
    };

    const onNext = async () => {
        if (isValid()) {
            /*
            let req = {
                cityId: "11313",
                countryId: 94,
                countryTelCode: "91",
                districtId: 8001,
                mobileNumber: "7654567899",
                stateId: "665"
            };
            */
            let req = val();
            let combinedReq = {
                ...req,
                ...stepsData[ADD_BENE_STEPS.PERSONAL_DETAILS.name]
            };
            console.log("!!!!!", combinedReq);

            try {
                setLoading(prev => ({ ...prev, submitBenePersonalInfo: true }));
                let res = await DataService.post("submitBenePersonalInfo", combinedReq);
                if (res.statusKey === "SUCCESS") {
                    let prefix = DataStore.getData().user.prefixCodeMobile;
                    let number = DataStore.getData().user.mobile;
                    let numberLengthToDisplay = parseInt(number.length / 2);
                    let maskedNumberLengthToDisplay = number.length - numberLengthToDisplay;
                    commitBeneDetails("", {
                        mobileNumber: `${prefix}${new Array(maskedNumberLengthToDisplay + 1).join("*")}${number.slice(
                            -numberLengthToDisplay
                        )}`
                    });
                } else {
                    let finalErrMsg = "";
                    if (res.messageKey.indexOf("VALIDATION_LENGTH_MOBILE") !== -1) {
                        let validationItems = res.messageKey.split(":");
                        let min = validationItems[1];
                        let max = validationItems[2];
                        let errorConfig = validationProfilesConfig.default(mobileNoFieldRef.current.val(), {
                            config: {
                                sizeRange: [min, max],
                                label: addBeneFieldConfigFunc().mobileNumber.field.label,
                                type: addBeneFieldConfigFunc().mobileNumber.field.type
                            }
                        });
                        if (errorConfig.errorMsg) {
                            finalErrMsg = errorConfig.errorMsg;
                        }
                    } else {
                        finalErrMsg = res.message;
                    }
                    ToastUtil.error(finalErrMsg);
                }
            } catch (error) {
                console.error("submitBenePersonalInfo failed", error);
            } finally {
                setLoading(prev => ({ ...prev, submitBenePersonalInfo: false }));
            }
        }
    };

    const commitBeneDetails = (contactType = "", options) => {
        let reqBase64 = "";
        if (contactType) {
            let reqObj = { contactType: contactType };
            reqBase64 = btoa(JSON.stringify(reqObj));
        }
        DataService.post(
            "validateOtpForSaveBene",
            {},
            {
                params: { "x-app-authd-params": reqBase64 },
                contactType: contactType,
                mobileNumber: options?.mobileNumber,
                otpSuccessCallback: resp => {
                    if (resp.statusKey === "SUCCESS") {
                        let beneSuccessData = {
                            activationTime:
                                DataStore.getData().config.newBeneTransactionTimeLimit || DEFAULT_ACTIVATION_TIME,
                            txLimit: `${
                                DataStore.getData().domCurrency ? DataStore.getData().domCurrency.quoteName : ""
                            } ${resp.data.beneTransactionAmountLimit || DEFAULT_TX_LIMIT}`,
                            fullLimit: `${
                                DataStore.getData().domCurrency ? DataStore.getData().domCurrency.quoteName : ""
                            } ${resp.data.beneChannelAmountLimit || DEFAULT_TX_LIMIT}`,
                            type: resp.data._message_type,
                            resp:resp
                        };
                        setFlowComplete(true);
                        MetaService.clear("getBeneficiaryList");
                        setBeneAdded(beneSuccessData);
                    }
                },
                otpFailureCallback: resp => {}
            }
        ).then(resp => {
            if (resp.messageKey === "JAX_FIELD_VALIDATION_FAILURE") {
                ToastUtil.error(resp.message);
            } else if (resp.messageKey === "CONTACT_TYPE_REQUIRED") {
                if (resp.meta.length) {
                    EventService.publish("modal.show", {
                        Component: OtpOptionSelectPopup,
                        props: {
                            containerClass: "new-otp-container",
                            contact:resp.meta,
                            callback:(data)=>{commitBeneDetails(data.contactType)}
                        }
                    });
                } else {
                    ToastUtil.error("Mobile number, Whatsapp number and Email not verified!");
                }
            } else if (
                OTP_STATUSES.indexOf(resp.statusKey) == -1 &&
                resp.statusKey !== "SUCCESS" &&
                OTP_LIMIT_STATUSES.indexOf(resp.statusKey) == -1 &&
                !["SMS_NOT_VERIFIED", "WHATSAPP_NOT_VERIFIED", "EMAIL_NOT_VERIFIED"].includes(resp.messageKey)
            ) {
                ToastUtil.error(resp.message);
            }
        });
    };

    const pushUniqueActiveField = (el, type, name, targetRefArray = activeFieldRefs) => {
        if (!el || !targetRefArray?.current) return;

        const existingIndex = targetRefArray.current.findIndex(ref => ref?.name === name);

        const fieldObject = {
            inputRef: { current: el },
            type,
            name
        };

        if (existingIndex !== -1) {
            targetRefArray.current[existingIndex] = fieldObject;
        } else {
            targetRefArray.current.push(fieldObject);
        }
    };

    let { mobileCode, mobileNumber, country, state, district, city, town } = addBeneFieldConfigFunc();
    prepare([mobileCode, mobileNumber, country, state, district, city, town]);

    if (beneAdded) return <AddBeneSuccess {...beneAdded} history={history} />;

    return (
        <>
            <div className={`fields-cont ${loading.submitBenePersonalInfo ? "loading-inline" : ""}`}>
                <div className="field-full">
                    <FormField
                        ref={countryFieldRef}
                        {...country}
                        containerClass={`${loading.fetchCountryList ? "input-loading" : ""}`}
                        options={countryList}
                        onChange={newVal => handleCountryFieldChange(newVal)}
                    />
                </div>
                <FormGroupWrapper formGroupWrapperRefs={activeFieldRefs.current} >
                <div className="field-full">
                    <FormField
                        // ref={stateFieldRef}
                        ref={el => {
                            stateFieldRef.current = el;
                            pushUniqueActiveField(el, state?.field?.type, state?.field?.name);
                        }}
                        {...state}
                        containerClass={`${loading.fetchStateList ? "input-loading" : ""}`}
                        options={stateList}
                        onChange={newVal => handleStateFieldChange(newVal)}
                    />
                </div>
                <div className="field-full">
                    <FormField
                        // ref={districtFieldRef}
                        ref={el => {
                            districtFieldRef.current = el;
                            pushUniqueActiveField(el, district?.field?.type, district?.field?.name);
                        }}
                        {...district}
                        containerClass={`${loading.fetchDistrictList ? "input-loading" : ""}`}
                        options={districtList}
                        onChange={newVal => handleDistrictFieldChange(newVal)}
                    />
                </div>
                <div className="field-full">
                    <FormField
                        // ref={cityFieldRef}
                        ref={el => {
                            cityFieldRef.current = el;
                            pushUniqueActiveField(el, city?.field?.type, city?.field?.name);
                        }}
                        {...city}
                        containerClass={`${loading.fetchCityList ? "input-loading" : ""}`}
                        options={cityList}
                        onChange={newVal => handleCityFieldChange(newVal)}
                    />
                </div>
                <div className="field-half">
                    <FormField
                        ref={mobileCodeFieldRef}
                        // ref={el => {
                        //     mobileCodeFieldRef.current = el;
                        //     pushUniqueActiveField(el, mobileCode?.field?.type, mobileCode?.field?.name);
                        // }}
                        {...mobileCode}
                        containerClass={`${loading.fetchCountryList ? "input-loading" : ""}`}
                        options={mobileCodeList}
                        onChange={newVal => handleMobileCodeFieldChange(newVal)}
                    />
                </div>
                <div className="field-half">
                    <FormField
                        // ref={mobileNoFieldRef}
                        ref={el => {
                            mobileNoFieldRef.current = el;
                            pushUniqueActiveField(el, mobileNumber?.field?.type, mobileNumber?.field?.name);
                        }}
                        {...mobileNumber}
                        onChange={newVal => handleMobileNoFieldChange(newVal)}
                    />
                </div>
                {enableTown && (
                    <div className="field-full">
                        <FormField
                            // ref={townFieldRef}
                            ref={el => {
                                townFieldRef.current = el;
                                pushUniqueActiveField(el, town?.field?.type, town?.field?.name);
                            }}
                            {...town}
                            containerClass={`${loading.fetchTownList ? "input-loading" : ""}`}
                            options={townList}
                            onChange={newVal => handleTownFieldChange(newVal)}
                        />
                    </div>
                )}
                </FormGroupWrapper>
            </div>
            <div className="btn-group">
                <button
                    className="primary-btn btn"
                    onClick={() => {
                        goToStep(ADD_BENE_STEPS.PERSONAL_DETAILS);
                    }}
                >
                    {I18n("g.action.back")}
                </button>
                <button
                    className="success-btn btn"
                    onClick={() => {
                        onNext();
                    }}
                >
                    {I18n("g.action.next")}
                </button>
            </div>
        </>
    );
};

export default ContactDetails;

export const AddBeneSuccess = props => {
    const contactNumber = DataStore.getData().tenant === "KWT" ? "  1840123" : DataStore.getData().tenant === "OMN" ? "  +968 9611 7900" : "";
    return (
        <>
            <img
                src={require(`@app/styles/img/success-img.svg`)}
                alt="logo"
                style={{ marginTop: "200px", marginBottom: "100px" }}
            />
            <span className="addBene-success-subText" style={{ fontWeight: "900" }}>
                {I18n("g.msg.benesuccess")}
            </span>
            <span className="addBene-success-subText" style={{ fontWeight: "900" }}>
                {I18n("g.lbl.thx", TenantUtils.getTenant())}
            </span>
            {DataStore.getData().tenant === "KWT" ? (
                <div style={{ marginTop: "20px" }}>
                    <img src={require("./whatsapp-qrcode.png")} />
                </div>
            ) : (
                DataStore.getData().tenant === "OMN" && (
                    <div style={{ marginTop: "20px" }}>
                        <img src={require("./omn-qrcode.png")} />
                    </div>
                )
            )}
            <span
                className="addBene-success-subText"
                style={{ fontWeight: "300", letterSpacing: "normal", fontSize: "30px", textAlign: "center" }}
            >
                {I18n("g.msg.visitus")}
                <span style={{ fontWeight: "900", color: " #006141" }}>{contactNumber}</span>
            </span>
            <button
                className="success-btn btn"
                style={{ width: "478px", margin: "140px 140px 30px 140px" }}
                onClick={() => props.history.push("/ibs-kiosk/app/remittance/choose-payment-mode",{idNo:props.resp.data.beneficaryRelationSeqId})}

            >
                {I18n("g.action.paynow")}
            </button>
            <button
                className="success-btn btn"
                style={{ width: "478px", margin: "0 140px 140px 140px" }}
                onClick={() => props.history.push("/ibs-kiosk/app/remittance/choose-bene")}
            >
                {I18n("g.lbl.beneficiary")}
            </button>
        </>
    );
};
